/**
 * MutationsProvider — injeta toast adapter + Sentry handler para useDomainMutation.
 *
 * Uso (uma unica vez por app, em `apps/<app>/src/main.tsx` ou App root):
 *
 *   import { MutationsProvider } from "@pps/mutations";
 *   import { useToast } from "@pps/ui/hooks/use-toast";
 *
 *   function Root() {
 *     const { toast } = useToast();
 *     return (
 *       <MutationsProvider
 *         toast={({ title, description, variant }) =>
 *           toast({
 *             title,
 *             description,
 *             variant: variant === "error" ? "destructive" : "default",
 *           })
 *         }
 *       >
 *         <App />
 *       </MutationsProvider>
 *     );
 *   }
 *
 * Sem provider, useDomainMutation continua funcional (logger + Sentry rodam),
 * apenas nao mostra toast — util pra testes Vitest sem DOM.
 *
 * Alinhado com IR-OBS-003 (todo erro relevante -> Sentry com tags) e
 * IR-OBS-006 (logger central via @pps/observability).
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ToastAdapter } from "./types";

interface MutationsContextValue {
  toast: ToastAdapter | null;
}

const MutationsContext = createContext<MutationsContextValue>({ toast: null });

export interface MutationsProviderProps {
  children: ReactNode;
  /** Adapter de toast — recebe `{ title, description, variant }`. */
  toast?: ToastAdapter;
}

export function MutationsProvider({ children, toast }: MutationsProviderProps) {
  const value = useMemo<MutationsContextValue>(
    () => ({ toast: toast ?? null }),
    [toast],
  );
  return (
    <MutationsContext.Provider value={value}>
      {children}
    </MutationsContext.Provider>
  );
}

/** Hook interno usado por useDomainMutation. Nao exportado no index publico. */
export function useMutationsContext(): MutationsContextValue {
  return useContext(MutationsContext);
}
